import { create } from "zustand";
import { devtools } from "zustand/middleware";

import {
  IGetManyOrganiserCompanyQueryReq,
  IGetOneOrganiserCompanyByShowIdQueryReq,
  IGetOneOrganiserCompanyQueryReq,
  IGetOneOrganiserCompanyRes,
  IUpdateOrganiserCompanyReq,
  IUpdateOrganiserCompanyRes,
} from "@evennt-tech/api-v2-external";
import { apiV2 } from "api/firebase";
import { IOrganiserCompany, NotificationType } from "@evennt-tech/api-v2-core";
import { getErrorMessage } from "tools/error";
import { registerStore } from "store/utils/reset-store.utils";

type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
};

type State = {
  itemsForAdmin: IOrganiserCompany[];
  paginationForItemsForAdmin: PaginationMeta;
  items: IOrganiserCompany[];
  selected: IGetOneOrganiserCompanyRes | null;
  isLoading: boolean;
  error: string | null;
  lastUsedAdminParams: IGetManyOrganiserCompanyQueryReq | null;
};

type Action = {
  fetchMany: (params: IGetManyOrganiserCompanyQueryReq) => Promise<void>;

  reload: () => void;
  autoCatch: () => void;
  fetchOne: (params: IGetOneOrganiserCompanyQueryReq) => Promise<void>;
  fetchOneByShowId: (
    params: IGetOneOrganiserCompanyByShowIdQueryReq
  ) => Promise<void>;
  edit: (
    body: IUpdateOrganiserCompanyReq
  ) => Promise<IUpdateOrganiserCompanyRes | undefined>;
  setSelected: (data: IGetOneOrganiserCompanyRes | null) => void;
  getSelected: () => IGetOneOrganiserCompanyRes | null;
  setError: (message: string | null) => void;
};

export const useOrganiserCompany = create<State & Action>()(
  devtools((set, get) => ({
    itemsForAdmin: [],
    paginationForItemsForAdmin: { page: 1, limit: 0, total: 0 },
    lastUsedAdminParams: null,

    selected: null,
    isLoading: false,
    items: [],

    error: null,

    setError: (message) => set({ error: message }),

    setSelected: (data) => set({ selected: data }),
    getSelected: () => get().selected,

    fetchMany: async (params) => {
      set({ isLoading: true, lastUsedAdminParams: params });
      try {
        const res = await apiV2.organiserCompany.getMany(params);
        const paginationData = {
          page: res.meta.currentPage,
          limit: res.meta.itemsPerPage,
          total: res.meta.totalItems ?? 0,
        };

        set({
          itemsForAdmin: res.items ?? [],
          paginationForItemsForAdmin: paginationData,
          error: null,
        });
      } catch (e) {
        console.error("Failed to fetch organiser company:", e);
      } finally {
        set({ isLoading: false });
      }
    },

    reload: async () => {
      const { fetchMany, lastUsedAdminParams } = get();

      if (lastUsedAdminParams) {
        await fetchMany(lastUsedAdminParams);
      }
    },

    autoCatch: async () => {
      const { reload } = get();
      const keys = [
        NotificationType.CREATED_ORGANISER_COMPANY,
        NotificationType.UPDATED_ORGANISER_COMPANY,
        NotificationType.DELETED_ORGANISER_COMPANY,
        NotificationType.PAID_FEATURES_DELETED,
        NotificationType.PAID_FEATURES_CREATED,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          callback: () => reload(),
        });
      }
    },

    fetchOne: async (params) => {
      set({ isLoading: true });
      try {
        const data = await apiV2.organiserCompany.getOne(params);
        set({ selected: data, error: null });
      } catch (e) {
        console.error("Failed to fetch organiser company:", e);
        set({ selected: null });
      } finally {
        set({ isLoading: false });
      }
    },

    fetchOneByShowId: async (params) => {
      set({ isLoading: true });
      try {
        const data = await apiV2.organiserCompany.getOneByShowId(params);
        set({ selected: data, error: null });
      } catch (e) {
        console.error("Failed to fetch organiser company by showId:", e);
        set({ selected: null });
      } finally {
        set({ isLoading: false });
      }
    },

    edit: async (body) => {
      try {
        const res = await apiV2.organiserCompany.edit(body);
        set((state) => ({
          items: state.items.map((item) => (item.id === res.id ? res : item)),
          itemsForAdmin: state.itemsForAdmin.map((item) =>
            item.id === res.id
              ? { ...item, isShowListAllowed: res.isShowListAllowed }
              : item
          ),
          error: null,
        }));
        return res;
      } catch (e) {
        console.error("Failed to edit organiser company:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },
  }))
);

registerStore(useOrganiserCompany);
